const LEVEL_BACKGROUND = Object.freeze({
  unit: "#f8f8f8",
  cell: "#f0f0f0",
  step: "#e8e8e8",
  substep: "#e0e0e0",
  service: "#d8d8d8",
});

const LEVEL_MARGIN = Object.freeze({
  unit: "0px",
  cell: "8px",
  step: "16px",
  substep: "24px",
  service: "32px",
});

export const EapTreeRowIdentity = ({
  label,
  eyebrow,
  expandable = false,
  expanded = false,
  onToggle,
  childrenId,
  toggleRef,
  title = null,
  state = null,
  details = null,
  actions = null,
  sticky = false,
  identityClassName = "",
  actionsClassName = "",
  style,
}) => (
  <div
    className={[
      "flex",
      "align-center",
      "gap-8",
      sticky ? "accordion-sticky-header" : "",
      "eap-tree-row__header",
    ]
      .filter(Boolean)
      .join(" ")}
    style={style}
  >
    {expandable ? (
      <button
        ref={toggleRef}
        type="button"
        className={`accordion-toggle ${expanded ? "active" : ""}`}
        aria-expanded={expanded}
        aria-controls={childrenId}
        aria-label={`${expanded ? "Recolher" : "Expandir"} ${label}`}
        onClick={onToggle}
      />
    ) : null}
    <div
      className={["grid", "eap-tree-row__identity", identityClassName]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="eap-tree-row__eyebrow">{eyebrow}</span>
      {title ?? (
        <span className="accordion-title" title={label}>
          {label}
        </span>
      )}
      {state ? <span className="eap-tree-row__state">{state}</span> : null}
      {details}
    </div>
    {actions ? (
      <div
        className={["eap-tree-row__actions", actionsClassName]
          .filter(Boolean)
          .join(" ")}
      >
        {actions}
      </div>
    ) : null}
  </div>
);

const EapTreeRow = ({
  level,
  label,
  eyebrow,
  state = null,
  expandable = false,
  expanded = false,
  onToggle,
  actions = null,
  details = null,
  content = null,
  tail = null,
  after = null,
  variant = "timeline",
  flat = false,
  rowId,
  childrenId,
  tabIndex,
  ariaCurrent,
  onKeyDown,
  onFocus,
  toggleRef,
  title = null,
  identityClassName = "",
  actionsClassName = "",
  rowClassName = "",
  containerClassName = "",
  containerStyle,
  bodyStyle,
  identityStyle,
}) => {
  const backgroundColor = LEVEL_BACKGROUND[level] ?? LEVEL_BACKGROUND.unit;
  const summary = variant === "summary" || variant === "singleColumn";
  const baseClassName =
    containerClassName || (summary ? "accordion-item" : "item");
  const timelineMargin = flat
    ? {}
    : { marginLeft: LEVEL_MARGIN[level] ?? "0px" };
  const resolvedContainerStyle = summary
    ? containerStyle
    : { ...timelineMargin, ...containerStyle };
  const identity = (
    <EapTreeRowIdentity
      label={label}
      eyebrow={eyebrow}
      expandable={expandable}
      expanded={expanded}
      onToggle={onToggle}
      childrenId={childrenId}
      toggleRef={toggleRef}
      title={title}
      state={summary ? null : state}
      details={summary ? null : details}
      actions={summary ? null : actions}
      sticky={!summary}
      identityClassName={identityClassName}
      actionsClassName={actionsClassName}
      style={{ backgroundColor, ...identityStyle }}
    />
  );

  return (
    <div
      id={rowId}
      tabIndex={tabIndex}
      aria-current={ariaCurrent}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      className={[
        baseClassName,
        "eap-tree-row",
        variant === "singleColumn" ? "eap-tree-row--single-column" : "",
        flat ? "eap-tree-row--flat" : "",
        rowClassName,
      ]
        .filter(Boolean)
        .join(" ")}
      style={resolvedContainerStyle}
    >
      <div
        className="accordion-item-body"
        style={{
          backgroundColor,
          ...(summary ? {} : { display: "block" }),
          ...bodyStyle,
        }}
      >
        {summary ? (
          <>
            <div style={identityStyle}>{identity}</div>
            {content ??
              (state || details || actions ? (
                <div className="column-span-2-tablet">
                  <div className="flex wrap-wrap wrap-nowrap-tablet gap-16 align-center">
                    <div className="eap-tree-row__summary-content">
                      {state ? (
                        <span className="eap-tree-row__state">{state}</span>
                      ) : null}
                      {details}
                    </div>
                    {actions ? (
                      <div
                        className={["eap-tree-row__actions", actionsClassName]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {actions}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null)}
          </>
        ) : (
          <div className="column-span-2-tablet">
            <div className="flex wrap-wrap wrap-nowrap-tablet gap-16 align-center">
              {identity}
              {tail}
            </div>
          </div>
        )}
      </div>
      {after}
    </div>
  );
};

export default EapTreeRow;
